import { NSARouterMakerResult } from "namirasoft-account-react";
import { ChatInferencerType, InferencerType, RaceFullRow, RaceInputRow, RaceRow, RaceTargetInputRow } from "namirasoft-inference";
import { NSBox, NSBoxString, NSBoxTextArea, NSColumn, NSRepeater, NSRow, NSSpace, NSSpaceSizeType } from "namirasoft-site-react";
import { Component, createRef } from "react";
import ChatList from "../components/ChatList";
import InferencerCommonFields from "../components/InferencerCommonFields";
import InferencerGraph from "../components/InferencerGraph";
import InferencerLoader from "../components/InferencerLoader";
import InferencerVisualizer from "../components/InferencerVisualizer";
import RaceTargetLine from "../components/RaceTargetLine";
import UsageChart from "../components/UsageChart";
import { InferenceEntities } from "../InferenceEntities";
import { InferenceInfo } from "../InferenceInfo";
import { InferenceLayout } from '../InferenceLayout';
import { InferenceRouterProps } from "../InferenceRouterProps";

interface RaceEditPageProps extends NSARouterMakerResult, InferenceRouterProps
{
    isEdit: boolean;
}

export default class RaceEditPage extends Component<RaceEditPageProps>
{
    NSRepeater_Targets = createRef<NSRepeater<RaceTargetLine, RaceTargetInputRow>>();
    NSBoxString_Name = createRef<NSBoxString>();
    InferencerCommonFields_Ids = createRef<InferencerCommonFields>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    override render()
    {
        return (
            <InferenceLayout<RaceRow, RaceInputRow>
                {...this.props}
                action={{ description: this.props.isEdit ? InferenceInfo.race.edit : InferenceInfo.race.create }}
                pages={{
                    entity: InferenceEntities.race(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_view: {
                                tabs: {
                                    extra: {
                                        after_general: [
                                            {
                                                title: "Chats",
                                                clicked: false,
                                                isVisible: (model) => !!model.id,
                                                getContent: (model) => model.id
                                                    ? <ChatList
                                                        {...this.props}
                                                        inferencer_type={ChatInferencerType.RACE}
                                                        inferencer_id={model.id}
                                                    />
                                                    : null,
                                            },
                                            {
                                                title: "Usage",
                                                clicked: false,
                                                isVisible: (model) => !!model.id,
                                                getContent: (model) => model.id
                                                    ? <UsageChart
                                                        {...this.props}
                                                        inferencer_type={InferencerType.RACE}
                                                        inferencer_id={model.id}
                                                    />
                                                    : null,
                                            },
                                        ],
                                    },
                                },
                            },
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): RaceInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let { validator_id, budget_id, memory_id, wiper_id, log_group_id } = this.InferencerCommonFields_Ids.current?.getValue() ?? InferencerCommonFields.EMPTY;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? null;
                                        let targets = this.NSRepeater_Targets.current?.getValue() ?? [];

                                        return {
                                            workspace_id: null,
                                            name,
                                            validator_id,
                                            budget_id,
                                            memory_id,
                                            wiper_id,
                                            log_group_id,
                                            description,
                                            targets,
                                            race_category: [],
                                            race_field: [],
                                            race_tag: [],
                                        };
                                    },
                                    setRow: (row: RaceRow) =>
                                    {
                                        let full = row as RaceFullRow;

                                        this.NSBoxString_Name.current?.setValue(full.name);
                                        this.InferencerCommonFields_Ids.current?.setValue(full);
                                        this.NSBoxTextArea_Description.current?.setValue(full.description);
                                        this.NSRepeater_Targets.current?.setValue((full.targets ?? []).map(t => ({
                                            inferencer_type: t.inferencer_type,
                                            inferencer_id: t.inferencer_id,
                                        })));
                                    }
                                },
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Targets",
                                            clicked: true,
                                            getContent: () => (
                                                <NSRepeater<RaceTargetLine, RaceTargetInputRow>
                                                    ref={this.NSRepeater_Targets}
                                                    title="Targets"
                                                    required
                                                    min={1}
                                                    createItem={(gIndex: number, lIndex: number, ref: React.RefObject<RaceTargetLine>, onChange: () => void, onDelete: undefined | ((index: number) => void)) =>
                                                    {
                                                        return <RaceTargetLine
                                                            ref={ref}
                                                            {...this.props}
                                                            key={`TargetLine_${gIndex}`}
                                                            index={lIndex}
                                                            deletable={true}
                                                            onChanged={() => { onChange(); }}
                                                            onDelete={() => { onDelete?.(lIndex); }}
                                                        />;
                                                    }}
                                                    isEmpty={function (_, value: RaceTargetInputRow | null): boolean
                                                    {
                                                        return !RaceTargetLine.IS_DIRTY(value);
                                                    }}
                                                    getValue={function (item: RaceTargetLine, checkError: boolean): RaceTargetInputRow | null
                                                    {
                                                        return item.getValue(checkError);
                                                    }}
                                                    setValue={function (item: RaceTargetLine, _: number, value: RaceTargetInputRow | null): void
                                                    {
                                                        if (value)
                                                            item.setValue(value);
                                                    }}
                                                    style={{ width: "100%", maxWidth: NSBox.width.double }}
                                                />
                                            )
                                        },
                                        {
                                            title: "Common",
                                            clicked: true,
                                            getContent: () => (
                                                <InferencerCommonFields ref={this.InferencerCommonFields_Ids} {...this.props} info_entity="race" />
                                            ),
                                        },
                                    ]
                                }
                            },
                            getChildrenAfter: (id, _row) => id
                                ? <>
                                    <NSSpace size={NSSpaceSizeType.SMALL} />
                                    <InferencerLoader
                                        {...this.props}
                                        inferencer_type={InferencerType.RACE}
                                        inferencer_id={id}
                                        render={({ tree, loading, error }) => (
                                            <>
                                                <InferencerGraph tree={tree} loading={loading} error={error} />
                                                <NSSpace size={NSSpaceSizeType.SMALL} />
                                                <InferencerVisualizer tree={tree} loading={loading} error={error} />
                                            </>
                                        )}
                                    />
                                </>
                                : null,
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Name}
                            required
                            title="Name"
                            style={{ width: NSBox.width.double }}
                            info={{ link: "https://namirasoft.com/inference/console-guide/race/#field-name" }}
                        />
                    </NSRow>
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        title="Description"
                        required={false}
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/inference/console-guide/race/#field-description" }}
                    />
                </NSColumn>
            </InferenceLayout>
        );
    }
}